import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Search,
  Wrench,
  DollarSign,
  Layers,
  ScanLine,
  Activity,
  Radar,
  Loader2,
  CheckCircle2,
  ChevronRight,
  Play,
  Sparkles,
} from "lucide-react";
import { TIDS } from "@/constants/testIds";
import AnimeVideoGallery from "@/components/AnimeVideoGallery";
import Seo from "@/components/Seo";
import ChamberBadge from "@/components/ChamberBadge";

const HERO_IMG =
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxsaXZlJTIwY29uY2VydCUyMGNyb3dkJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3ODEwNDYxMzJ8MA&ixlib=rb-4.1.0&q=85";
const STUDIO_IMG =
  "https://images.unsplash.com/photo-1559732277-7453b141e3a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtdXNpY2lhbiUyMGluJTIwcmVjb3JkaW5nJTIwc3R1ZGlvfGVufDB8fHx8MTc4MTA0NjEzMXww&ixlib=rb-4.1.0&q=85";
const VINYL_IMG =
  "https://images.unsplash.com/photo-1526394931762-90052e97b376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHx2aW55bCUyMHJlY29yZCUyMHBsYXllcnxlbnwwfHx8YmxhY2t8MTc4MTA0NjE0MXww&ixlib=rb-4.1.0&q=85";

// Testimonials with photos (Mogul-style cards)
const TESTIMONIALS = [
  {
    quote:
      "E-Quantum found six figures in back-royalties I didn't even know existed. Publishing set up properly, PROs registered, splits documented — done in weeks.",
    name: "JAYDEN R.",
    handle: "@jaydenrofficial",
    role: "Independent Hip-Hop Artist",
    photo: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  },
  {
    quote:
      "Two Netflix syncs and an ad campaign inside three months. And I still own 100% of my masters and publishing. It's a career operating system.",
    name: "ARIA",
    handle: "@ariasound",
    role: "R&B / Pop Vocalist",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  },
  {
    quote:
      "E-Quantum locked in my publishing side, chased every royalty across every source, AND I still walked away owning 100% of my masters and 100% of my publishing. This is what artists actually needed.",
    name: "Drago Sound",
    handle: "@dragosound",
    role: "Producer / Songwriter",
    photo: "https://customer-assets-v7afamib.emergentagent.net/job_artist-earnings-hub-1/artifacts/ci72awdn_IMG_5301.jpeg",
  },
];

// Streaming destinations artists want to be on — not DIY-registration services.
// This signals reach + platform ubiquity without leaking the "I can do this myself" thought.
const PLATFORMS = [
  "Spotify",
  "Apple Music",
  "YouTube Music",
  "TIDAL",
  "Amazon Music",
  "SoundCloud",
  "Pandora",
];

const STATS = [
  ["17-step", "Artist Infrastructure Audit"],
  ["60+", "Royalty sources monitored"],
  ["100%", "Of your ownership stays with you"],
];

const FEATURES = [
  {
    icon: Layers,
    tag: "01 · One Portal",
    title: "One portal for every royalty source.",
    desc: "Publishing, mechanicals, sync, neighbouring rights, digital performance, Content ID — all under one roof, mapped to your catalog.",
  },
  {
    icon: Radar,
    tag: "02 · Identify Issues",
    title: "We find the gaps you can't see.",
    desc: "Our advanced Artist Infrastructure Audit™ scans every registration, split sheet, and metadata field across your catalog and pinpoints exactly where money is leaking.",
  },
  {
    icon: Wrench,
    tag: "03 · Fix Issues, Get Paid",
    title: "We handle the paperwork. You get paid.",
    desc: "We submit your registrations directly to BMI, ASCAP, SESAC, The MLC, and SoundExchange, and chase down back-royalties on your behalf.",
  },
  {
    icon: Activity,
    tag: "04 · Stay On Track",
    title: "Continuous monitoring, never miss a royalty.",
    desc: "New releases auto-registered. Every royalty statement parsed and reconciled. Real-time dashboard showing what you earned, what's pending, and what's next.",
  },
];

export default function Landing() {
  return (
    <div>
      <Seo
        title="Artist-First Music Consulting"
        description="E-Quantum Consults helps artists own, collect, track, monetize and amplify their intellectual property. Publishing registration, royalty recovery, distribution and sync placements."
        path="/"
      />
      <Hero />
      <PartnersRow />
      <ProcessSection />
      <StatsBar />
      <FoundersVideo />
      <FeaturesSection />
      <AnimeVideoGallery />
      <TestimonialsSection />
      <WhySplit />
      <ChecklistCapture />
      <FinalCTA />
    </div>
  );
}

/* -------------------------------------------------------- */
/*                          HERO                            */
/* -------------------------------------------------------- */
function Hero() {
  const nav = useNavigate();
  const [role, setRole] = useState("artist"); // 'artist' | 'writer'

  const handleStart = () => {
    nav(`/audit?role=${role}`);
  };

  return (
    <section className="relative min-h-[92vh] overflow-hidden flex items-end">
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Live concert crowd"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="absolute inset-0 dotted-grid opacity-30" />
      </div>

      <div className="absolute top-28 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="marquee whitespace-nowrap">
          <span className="text-outline text-[8rem] md:text-[12rem] leading-none pr-12">
            MUSIC · MONEY · POWER · OWN · COLLECT · TRACK · MONETIZE · AMPLIFY · PUBLISHING · ROYALTIES ·
          </span>
          <span className="text-outline text-[8rem] md:text-[12rem] leading-none pr-12">
            MUSIC · MONEY · POWER · OWN · COLLECT · TRACK · MONETIZE · AMPLIFY · PUBLISHING · ROYALTIES ·
          </span>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-24 lg:pb-32 grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
        <div className="md:col-span-9">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="tag mb-6"
          >
            <Sparkles size={12} /> Artist-First Music Consulting
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-heading font-black text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tighter text-white"
          >
            Your music.<br />
            Your money.<br />
            <span className="text-[var(--primary)]">Your power.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed"
          >
            We help artists <span className="text-white">own, collect, track, monetize</span> and{" "}
            <span className="text-[var(--primary)]">visually amplify</span> their intellectual property.
            Publishing side established, every royalty collected, placement on every major platform.
            Signature performance visuals and brand mythology that make the world remember your name.
          </motion.p>

          {/* Artist / Writer toggle — Mogul-style segmentation */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 max-w-lg"
          >
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-3">
              Start here
            </div>
            <div className="inline-flex border border-white/15 bg-black/30 backdrop-blur-sm p-1 mb-5" data-testid="hero-role-toggle">
              <button
                type="button"
                onClick={() => setRole("artist")}
                data-testid="hero-role-artist"
                className={`px-6 py-2.5 font-mono text-xs uppercase tracking-widest transition-all ${
                  role === "artist"
                    ? "bg-[var(--primary)] text-black font-bold"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                I&rsquo;m an artist
              </button>
              <button
                type="button"
                onClick={() => setRole("writer")}
                data-testid="hero-role-writer"
                className={`px-6 py-2.5 font-mono text-xs uppercase tracking-widest transition-all ${
                  role === "writer"
                    ? "bg-[var(--primary)] text-black font-bold"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                I&rsquo;m a writer
              </button>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed mb-5">
              If you&rsquo;re both an artist and a writer, pick which one you currently earn more royalties from.
              We&rsquo;ll account for both once you take the audit.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={handleStart}
                data-testid={TIDS.heroCta}
                className="inline-flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest font-bold text-black transition-all"
                style={{
                  background: "#22c55e",
                  boxShadow: "0 0 24px -4px rgba(34,197,94,0.55)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              >
                Take the Artist Infrastructure Audit&trade; <ArrowUpRight size={16} />
              </button>
              <Link to="/services" data-testid={TIDS.heroSecondary} className="btn-ghost">
                See what we do <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                    PARTNERS / TRUST                      */
/* -------------------------------------------------------- */
function PartnersRow() {
  return (
    <section className="border-y border-white/10 bg-[#070707] py-10" data-testid="trust-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-8">
        {/* Hollywood Chamber badge (official widget — clickable) */}
        <ChamberBadge />

        {/* Trusted by artists placed on: */}
        <div>
          <div className="text-center font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-6">
            // Trusted by artists placed on
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 md:gap-x-12 gap-y-4" data-testid="platforms-row">
            {PLATFORMS.map((p) => (
              <div
                key={p}
                className="font-heading font-black text-base md:text-xl tracking-tight text-zinc-400 hover:text-white transition-colors"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                    3-STEP PROCESS                        */
/* -------------------------------------------------------- */
function ProcessSection() {
  const steps = [
    {
      icon: Search,
      num: "01",
      title: "Scan",
      copy: "We find every registration gap and every uncollected royalty across your catalog. Publishing, mechanicals, neighbouring rights — nothing hidden.",
    },
    {
      icon: Wrench,
      num: "02",
      title: "Fix",
      copy: "We submit your registrations directly to BMI, ASCAP, SESAC, The MLC, and SoundExchange. We chase back-royalties on your behalf.",
    },
    {
      icon: DollarSign,
      num: "03",
      title: "Get Paid",
      copy: "Artists collect an average of 20%+ more in royalties once every gap is closed. Real-time dashboard tracks every dollar as it arrives.",
    },
  ];

  return (
    <section className="py-28 md:py-36" data-testid="process-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <div className="tag mb-4">// Here&rsquo;s how it works</div>
          <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter">
            Three steps between you and
            <br />
            <span className="text-[var(--primary)]">the money you&rsquo;re owed.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#0a0a0a] p-10 md:p-12 relative"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <div className="font-heading font-black text-5xl text-white/10 leading-none">
                    {s.num}
                  </div>
                </div>
                <h3 className="font-heading font-black text-3xl text-white mb-4 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {s.copy}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                        STATS BAR                         */
/* -------------------------------------------------------- */
function StatsBar() {
  return (
    <section
      className="relative border-y border-[var(--primary)]/30 bg-[#080808] overflow-hidden"
      data-testid="stats-bar"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(212,175,55,0.6), transparent 70%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
        {STATS.map(([num, label]) => (
          <div key={label} className="py-12 px-4 md:px-8 text-center md:text-left">
            <div className="font-heading font-black text-5xl md:text-7xl text-[var(--primary)] tracking-tighter leading-none">
              {num}
            </div>
            <div className="mt-4 text-xs text-zinc-400 uppercase tracking-widest max-w-xs mx-auto md:mx-0">
              {label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                    FOUNDERS VIDEO                        */
/* -------------------------------------------------------- */
function FoundersVideo() {
  const VIDEO_URL = "/video/manifesto-v4.mp4";
  return (
    <section className="py-24 md:py-32 bg-[#050505]" data-testid="founders-video">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="tag mb-4 inline-flex">// The E-Quantum Manifesto</div>
          <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tighter">
            The music business is changing.<br />
            <span className="text-[var(--primary)]">Take control of yours.</span>
          </h2>
        </div>

        <div
          className="relative aspect-video border border-white/10 bg-[#0a0a0a] overflow-hidden"
          data-testid="founders-video-player"
        >
          <video
            src={VIDEO_URL}
            poster={STUDIO_IMG}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={VIDEO_URL} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                   4-FEATURE BLOCKS                       */
/* -------------------------------------------------------- */
function FeaturesSection() {
  return (
    <section className="py-28 md:py-36 bg-[#080808]" data-testid="features-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-6">
            <div className="tag mb-4">// Everything you need</div>
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Everything you need
              <br />
              <span className="text-[var(--primary)]">to run your music business.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
              One team. Every registration. Every royalty source. Every dollar accounted for —
              from the first stream to the last international performance.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#0a0a0a] p-10 md:p-12 min-h-[320px] flex flex-col"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
                    {f.tag}
                  </div>
                </div>
                <h3 className="font-heading font-black text-2xl md:text-3xl text-white mb-4 tracking-tight leading-tight">
                  {f.title}
                </h3>
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed flex-1">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                     TESTIMONIALS                         */
/* -------------------------------------------------------- */
function TestimonialsSection() {
  return (
    <section className="py-28 md:py-36" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-16 gap-8 flex-wrap">
          <div>
            <div className="tag mb-4">// Trusted by the best</div>
            <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter">
              Receipts.
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-md">
            Real outcomes from artists who decided to keep their power.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name}
              className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10 flex flex-col hover:border-[var(--primary)]/40 transition-colors"
              data-testid={`testimonial-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <ScanLine size={20} className="text-[var(--primary)] mb-6" strokeWidth={1.5} />
              <p className="text-zinc-200 text-base leading-relaxed flex-1 mb-8">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="border-t border-white/10 pt-5 flex items-center gap-4">
                <img
                  src={t.photo}
                  alt={t.name}
                  className="w-11 h-11 rounded-full object-cover border border-white/10"
                />
                <div>
                  <div className="font-heading font-bold text-sm text-white tracking-wide">
                    {t.name}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    {t.handle} · {t.role}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                       WHY SPLIT                          */
/* -------------------------------------------------------- */
function WhySplit() {
  return (
    <section className="relative py-24 md:py-32 bg-[#080808]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative">
          <div className="relative overflow-hidden">
            <img
              src={STUDIO_IMG}
              alt="Musician in studio"
              className="w-full h-[500px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-4 bg-[var(--primary)] text-black px-6 py-4 max-w-[220px]">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
              Independent
            </div>
            <div className="font-heading font-black text-2xl leading-tight">
              100% Artist-Owned
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 lg:pl-8">
          <div className="tag mb-4">// Why E-Quantum</div>
          <h2 className="font-heading font-black text-4xl md:text-5xl leading-[0.95] tracking-tighter text-white mb-8">
            Major-label
            <br />
            firepower.
            <br />
            <span className="text-[var(--primary)]">Without the label.</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed mb-8">
            The traditional model takes 50–85% of your earnings, your masters,
            and your decision-making. We rebuilt that model. You keep everything.
            We handle the publishing, the PROs, the collection, the placements
            and the strategy — for a transparent flat-rate or success-based fee.
          </p>
          <div className="space-y-3">
            {[
              "You retain 100% ownership of masters and publishing",
              "Transparent flat or success-based pricing — no equity grabs",
              "Real-time dashboard so you see every dollar move",
              "Direct access to your consultant — not a service ticket",
            ].map((line) => (
              <div key={line} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[var(--primary)] mt-2 flex-shrink-0" />
                <p className="text-zinc-300 text-sm leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/about" className="btn-ghost">
              Read our story <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*                      FINAL CTA                           */
/* -------------------------------------------------------- */
function FinalCTA() {
  return (
    <section className="relative py-32 overflow-hidden border-y border-white/10">
      <div className="absolute inset-0">
        <img src={VINYL_IMG} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/40" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <div className="tag mb-4">// Artist Infrastructure Audit&trade;</div>
          <h2 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-white">
            Score your music
            <br />
            <span className="text-[var(--primary)]">business in 4 minutes.</span>
          </h2>
          <p className="text-zinc-400 text-base md:text-lg mt-6 leading-relaxed max-w-xl">
            A 17-question audit that pinpoints exactly where money is leaking
            out of your business — and what to fix first. Free. Emailed to
            you instantly.
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <Link to="/audit" className="btn-primary" data-testid="final-cta-audit">
            Start the audit <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- */
/*             CHECKLIST CAPTURE (lead magnet)              */
/* -------------------------------------------------------- */
function ChecklistCapture() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email so we can send it over.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/checklist", { email, source: "landing_checklist" });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something jammed. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-24 md:py-32 bg-[#050505] border-t border-white/5 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(212,175,55,0.14), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[var(--primary)] mb-4">
            // The Royalty Recovery Checklist&trade;
          </div>
          <h2 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            7 income streams<br />
            <span className="text-[var(--primary)]">most artists</span> never claim.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg mt-6 leading-relaxed max-w-xl">
            Drop your email. We&rsquo;ll send you a one-page checklist of every
            royalty stream your music is likely generating &mdash; and which ones
            are still sitting in escrow with your name on them.
          </p>
          <p className="mt-4 font-mono text-[10px] tracking-widest text-zinc-600 uppercase">
            No spam. Unsubscribe with one tap.
          </p>
        </div>

        <div className="md:col-span-5">
          {sent ? (
            <div
              data-testid="checklist-success"
              className="border border-[var(--primary)]/50 bg-[var(--primary)]/5 p-8 md:p-10 text-center"
              style={{ boxShadow: "0 0 60px -20px rgba(212,175,55,0.4)" }}
            >
              <CheckCircle2 size={32} className="text-[var(--primary)] mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-heading font-black text-2xl tracking-tighter mb-2">
                Check your inbox.
              </h3>
              <p className="text-zinc-300 text-sm leading-relaxed mb-6">
                Your Royalty Recovery Checklist&trade; is on its way to{" "}
                <span className="text-white font-medium break-all">{email}</span>.
              </p>
              <Link to="/audit" className="btn-primary justify-center inline-flex" data-testid="checklist-audit-cta">
                Want a personalized score? Take the audit <ArrowUpRight size={14} />
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10"
              data-testid="checklist-form"
            >
              <label className="block font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-3">
                Your email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="checklist-email-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-3 text-white placeholder-zinc-600 text-base transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="checklist-submit-btn"
                className="w-full justify-center mt-6 inline-flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest font-bold text-black transition-all"
                style={{
                  background: "#22c55e",
                  boxShadow: "0 0 24px -4px rgba(34,197,94,0.55)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending&hellip;</>
                ) : (
                  <>Send me the checklist <ArrowUpRight size={16} /></>
                )}
              </button>
              <p className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest mt-4 text-center">
                Free. Instant. No credit card.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
